import { useState, useEffect, useCallback, useId, memo } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { Checkbox } from './Checkbox';
import { Message } from '../Message';

import type { CheckboxGroupProps, CheckboxGroupOption, CheckedStateMap, FontSizeValue } from './Checkbox.types';
import type { Theme } from '../../theme';

/**
 * Map theme font size token to actual value
 */
const getFontSizeValue = (fontSize: FontSizeValue | undefined, theme: Theme): string | undefined => {
  if (!fontSize) {
    return undefined;
  }
  
  // Check if it's a theme token key
  const themeTokens = ['xs', 'sm', 'base', 'md', 'lg', 'xl'] as const;
  if (themeTokens.includes(fontSize as typeof themeTokens[number])) {
    return theme.typography.fontSize[fontSize as keyof typeof theme.typography.fontSize];
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Fieldset wrapper for the checkbox group
 */
const Fieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

/**
 * Legend for the checkbox group
 */
const Legend = styled.legend<{ theme?: Theme; $fontSize?: string }>`
  padding: 0;
  margin-bottom: 8px;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $fontSize }) => $fontSize || theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

/**
 * Required asterisk indicator
 */
const RequiredIndicator = styled.span<{ theme?: Theme }>`
  color: ${({ theme }) => theme.colors.highlight};
  margin-left: 2px;
`;

/**
 * Container for the checkbox options
 */
const GroupContainer = styled.div<{
  $orientation: 'horizontal' | 'vertical';
  $isChild?: boolean;
}>`
  display: flex;
  gap: 8px;
  flex-direction: ${({ $orientation }) =>
    $orientation === 'horizontal' ? 'row' : 'column'};
  flex-wrap: ${({ $orientation }) =>
    $orientation === 'horizontal' ? 'wrap' : 'nowrap'};
  ${({ $isChild }) => $isChild && 'margin-left: 26px;'}
`;

/**
 * Container for messages
 */
const MessageContainer = styled.div`
  margin-top: 8px;
`;

/**
 * Initialize checked state from options
 */
function initializeCheckedState(options: CheckboxGroupOption[], defaultValue?: CheckedStateMap): CheckedStateMap {
  const state: CheckedStateMap = { ...defaultValue };
  
  const processOptions = (opts: CheckboxGroupOption[]) => {
    for (const option of opts) {
      if (state[option.id] === undefined) {
        state[option.id] = false;
      }
      if (option.children) {
        processOptions(option.children);
      }
    }
  };
  
  processOptions(options);
  return state;
}

/**
 * CheckboxGroup - A component for managing a group of checkboxes
 *
 * Handles multi-selection state and supports hierarchical checkbox structures
 * with parent-child relationships. Supports both controlled and uncontrolled modes.
 *
 * @example
 * ```tsx
 * // Controlled
 * <CheckboxGroup
 *   name="toppings"
 *   value={selectedToppings}
 *   onChange={(state) => setSelectedToppings(state)}
 *   options={[
 *     { id: 'cheese', label: 'Cheese' },
 *     { id: 'pepperoni', label: 'Pepperoni' },
 *     { id: 'mushrooms', label: 'Mushrooms' },
 *   ]}
 *   legend="Select Toppings"
 * />
 *
 * // With hierarchical options
 * <CheckboxGroup
 *   options={[
 *     { 
 *       id: 'all', 
 *       label: 'Select All',
 *       children: [
 *         { id: 'option1', label: 'Option 1' },
 *         { id: 'option2', label: 'Option 2' },
 *       ]
 *     },
 *   ]}
 *   orientation="vertical"
 * />
 * ```
 */
const CheckboxGroupComponent = ({
  name,
  value: controlledValue,
  defaultValue,
  onChange,
  options,
  disabled = false,
  error = false,
  orientation = 'vertical',
  legend,
  'aria-label': ariaLabel,
  required = false,
  errorMessage,
  assistiveMessage,
  className,
  'data-testid': testId,
  labelFontSize,
  optionsFontSize,
}: CheckboxGroupProps) => {
  const generatedId = useId();
  const groupId = name ? `checkboxgroup-${name}` : `checkboxgroup-${generatedId}`;
  const theme = useTheme() as Theme;
  
  // Resolve font sizes
  const resolvedLabelFontSize = getFontSizeValue(labelFontSize, theme);
  const resolvedOptionsFontSize = getFontSizeValue(optionsFontSize, theme);

  // Determine if controlled or uncontrolled
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<CheckedStateMap>(() =>
    initializeCheckedState(options, defaultValue)
  );
  const checkedState = isControlled ? controlledValue : internalValue;

  // Sync internal state with controlled value
  useEffect(() => {
    if (isControlled) {
      setInternalValue(controlledValue);
    }
  }, [isControlled, controlledValue]);

  // Reinitialize when options change
  useEffect(() => {
    if (!isControlled) {
      setInternalValue(prev => initializeCheckedState(options, prev));
    }
  }, [options, isControlled]);

  const handleCheckboxChange = useCallback(
    (optionId: string, isChecked: boolean, childrenIds?: string[]) => {
      const newState = { ...checkedState, [optionId]: isChecked };
      
      // If this option has children, update them all
      if (childrenIds) {
        for (const childId of childrenIds) {
          newState[childId] = isChecked;
        }
      }
      
      if (!isControlled) {
        setInternalValue(newState);
      }
      onChange?.(newState);
    },
    [checkedState, isControlled, onChange]
  );

  // Get all children IDs for a given option
  const getAllChildrenIds = useCallback((option: CheckboxGroupOption): string[] => {
    const ids: string[] = [];
    if (option.children) {
      for (const child of option.children) {
        ids.push(child.id);
        ids.push(...getAllChildrenIds(child));
      }
    }
    return ids;
  }, []);

  const renderCheckboxOptions = (
    items: CheckboxGroupOption[],
    _isChild = false
  ) => {
    return items.map((option) => {
      const childrenIds = getAllChildrenIds(option);
      
      return (
        <div key={option.id}>
          <Checkbox
            name={option.id}
            label={option.label}
            checked={checkedState[option.id] || false}
            disabled={option.disabled || disabled}
            error={option.error || error}
            onChange={(_, checked) => handleCheckboxChange(option.id, checked, childrenIds.length > 0 ? childrenIds : undefined)}
            data-testid={testId && `${testId}-option-${option.id}`}
            labelFontSize={resolvedOptionsFontSize}
          />
          {option.children && option.children.length > 0 && (
            <GroupContainer $orientation="vertical" $isChild>
              {renderCheckboxOptions(option.children, true)}
            </GroupContainer>
          )}
        </div>
      );
    });
  };

  return (
    <Fieldset className={className} data-testid={testId}>
      {legend && (
        <Legend $fontSize={resolvedLabelFontSize}>
          {legend}
          {required && <RequiredIndicator>*</RequiredIndicator>}
        </Legend>
      )}
      <GroupContainer
        role="group"
        aria-labelledby={legend ? groupId : undefined}
        aria-label={!legend ? ariaLabel : undefined}
        $orientation={orientation}
        data-testid={testId && `${testId}-group`}
      >
        {renderCheckboxOptions(options)}
      </GroupContainer>
      {errorMessage && error && (
        <MessageContainer>
          <Message variant="error">{errorMessage}</Message>
        </MessageContainer>
      )}
      {assistiveMessage && !errorMessage && (
        <MessageContainer>
          <Message variant="assistive">{assistiveMessage}</Message>
        </MessageContainer>
      )}
    </Fieldset>
  );
};

CheckboxGroupComponent.displayName = 'CheckboxGroup';

export const CheckboxGroup = memo(CheckboxGroupComponent);

export default CheckboxGroup;
